import React, { useEffect, useRef, useState } from "react";
import { BellRing, Loader2, ArrowLeft } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { getGuest, isIpadMode } from "@/lib/guest";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import GuestVoiceMessage from "@/components/GuestVoiceMessage";
import GuestRequestReceipt from "@/components/GuestRequestReceipt";
import GuestTextMessage from "@/components/GuestTextMessage";
import ServiceCallWheel from "@/components/ServiceCallWheel";

// Stage one shares the physical call/Watch request queue and its entitlement.
// No phone number or external messaging account is needed.
const MODULE_ID = "guest_requests";

export default function GuestCallButton({ cabinView = false }) {
  const guest = getGuest();
  const ipad = isIpadMode();
  const eligible = Boolean(guest.cabin_id && (ipad ? guest.roster.length : guest.id));
  const accountGuestId = ipad ? (guest.roster.find((r) => r.guest_id === guest.id)?.guest_id || (guest.roster.length === 1 ? guest.roster[0].guest_id : "")) : guest.id;
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [guestId, setGuestId] = useState(accountGuestId || "");
  const [sending, setSending] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [textBusy, setTextBusy] = useState(false);
  const [panel, setPanel] = useState("service");
  const [sent, setSent] = useState("");
  const [activeCalls, setActiveCalls] = useState({});
  const [lastReceipt, setLastReceipt] = useState(null);
  useEffect(() => { if (sent?.id) setLastReceipt(sent); }, [sent]);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const voiceControl = useRef(null);
  const [voiceState, setVoiceState] = useState({ phase: "idle", ready: false });
  useEffect(() => {
    if (["processing", "review", "sending"].includes(voiceState.phase) || voiceState.error) setPanel("voice");
  }, [voiceState.phase, voiceState.error]);

  useEffect(() => { setGuestId(accountGuestId || ""); }, [accountGuestId, cabinView]);
  useEffect(() => {
    if (open && panel === "text") document.getElementById("guest-message-text")?.focus();
  }, [open, panel]);

  useEffect(() => {
    if (!eligible) { setAvailable(false); return; }
    let alive = true;
    let checking = false;
    const refresh = async () => {
      if (navigator.onLine === false) { if (alive) setAvailable(false); return; }
      if (checking) return;
      checking = true;
      try {
        const { data } = await api.get("/public/modules");
        if (alive) setAvailable(navigator.onLine !== false && (data.items || []).some((m) => m.id === MODULE_ID && m.operational_enabled));
      } catch { if (alive) setAvailable(false); }
      finally { checking = false; }
    };
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    const onOffline = () => setAvailable(false);
    refresh();
    const timer = setInterval(refresh, 15000);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [eligible, guest.cabin_id]);

  useEffect(() => {
    setActiveCalls({});
    if (!eligible || !available || !guestId) return;
    let alive = true, checking = false;
    const controller = new AbortController();
    const refresh = async () => {
      if (checking || navigator.onLine === false || document.visibilityState === "hidden") return;
      checking = true;
      try {
        const { data } = await api.get("/public/platform/requests/active", { params: { guest_id: guestId }, signal: controller.signal });
        if (alive) { const calls = {}; for (const item of data.items || []) if (!calls[item.title] && ["new","accepted","in_progress"].includes(item.status)) calls[item.title] = item; setActiveCalls(calls); }
      } catch { /* Keep last confirmed active appearance during interruption. */ }
      finally { checking = false; }
    };
    refresh(); const timer = setInterval(refresh,2000); document.addEventListener("visibilitychange",refresh); window.addEventListener("online",refresh);
    return () => { alive = false; controller.abort(); clearInterval(timer); document.removeEventListener("visibilitychange",refresh); window.removeEventListener("online",refresh); };
  }, [eligible, available, guestId, guest.cabin_id]);

  const changeOpen = (next) => {
    if (inFlight.current || voiceBusy || textBusy) return;
    if (next) {
      setGuestId((current) => cabinView && guest.roster.some((r) => r.guest_id === current) ? current : accountGuestId);
      setSent("");
      setError("");
      setPanel("service");
    }
    setOpen(next);
  };

  const call = async (title) => {
    if (inFlight.current || voiceBusy || textBusy || !guestId || !available || sent) return;
    inFlight.current = true;
    setSending(true);
    setError("");
    try {
      // The API binds identity to the guest cookie / cabin device session and
      // rechecks the entitlement, including changes since the last poll.
      const { data } = await api.post("/public/platform/requests", {
        module_id: MODULE_ID,
        guest_id: guestId,
        title,
        priority: "normal",
      });
      setActiveCalls((current) => ({ ...current, [title]: { id: data.id, title, status: data.status || "new" } }));
      setSent({ title, id: data.id, status: data.status, guestId, repeated: data.already_called, elapsedSeconds: data.elapsed_seconds });
    } catch (err) {
      if (err.response?.status === 409) setAvailable(false);
      setError(formatErr(err.response?.data?.detail) || "Delivery could not be confirmed. Please check with the crew before trying again.");
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  const hasActiveCall = Object.values(activeCalls).some((item) => ["new", "accepted", "in_progress"].includes(item.status));
  if (!eligible || (!available && !open)) return null;

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      {available && <DialogTrigger asChild>
        <Button className="guest-call-trigger rounded-full min-h-11 shrink-0 gap-2 px-3 sm:px-4" aria-label="Call crew" data-active={hasActiveCall ? "true" : "false"} aria-describedby={hasActiveCall ? "guest-call-active-status" : undefined} data-testid="guest-call-button">
          <BellRing className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">{hasActiveCall ? "Call active" : "Call crew"}</span>
          <span className="sm:hidden">{hasActiveCall ? "Active" : "Call"}</span>
          {hasActiveCall && <span id="guest-call-active-status" className="sr-only">An active call is awaiting crew completion. Open to view or remind crew.</span>}
        </Button>
      </DialogTrigger>}
      <DialogContent data-slx-update-busy={open ? "true" : "false"} className="service-dialog max-h-[90dvh] overflow-y-auto" onInteractOutside={(e) => { if (sending || voiceBusy || textBusy) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (sending || voiceBusy || textBusy) e.preventDefault(); }}>
        <DialogHeader>
          <p className="service-dialog__brand">SLX-one <span aria-hidden="true">/</span> private service</p>
          <DialogTitle className="font-display text-3xl text-center">At your service</DialogTitle>
          <DialogDescription className="text-center text-xs">Your crew, one touch away. Your cabin and caller details travel with every request.</DialogDescription>
        </DialogHeader>
        {sent ? <GuestRequestReceipt onBusyChange={setVoiceBusy} receipt={sent} cabin={guest.cabin} onDone={() => changeOpen(false)} /> : <div className="space-y-4 pt-2">
          {ipad && cabinView && guest.roster.length > 1 && <fieldset>
            <legend className="text-sm font-medium">Who is calling? <span className="font-normal text-muted-foreground">(optional)</span></legend>
            <p className="text-xs text-muted-foreground mt-1">Your selected account is used unless you choose another guest.</p>
            <div className="grid grid-cols-2 gap-2 mt-3">{guest.roster.map((r) => <Button key={r.guest_id} variant="outline" type="button"
              aria-pressed={guestId === r.guest_id} disabled={sending || voiceBusy || textBusy}
              onClick={() => setGuestId(guestId === r.guest_id ? accountGuestId : r.guest_id)}
              className={`h-auto min-h-12 whitespace-normal text-left justify-start rounded-xl ${guestId === r.guest_id ? "border-accent bg-accent/10 text-foreground ring-1 ring-accent/40" : ""}`}
              data-testid={`call-guest-tile-${r.guest_id}`}>{r.guest_name}</Button>)}</div>
          </fieldset>}
          {!guestId && !(ipad && cabinView) && <p className="text-sm text-muted-foreground">Choose your guest account in Cabin view before sending a request.</p>}
          {!available && <p role="status" className="text-sm text-muted-foreground">Crew calling is currently unavailable. Please contact the crew in person.</p>}
          {panel === "service" ? <ServiceCallWheel disabled={!available || !guestId || sending || voiceBusy || textBusy} sending={sending} activeCalls={activeCalls}
            onCall={() => call("Call steward")} onHousekeeping={() => call("Housekeeping")}
            onText={() => setPanel("text")} voiceState={voiceState}
            onVoiceStart={() => voiceControl.current?.startHold()} onVoiceEnd={() => voiceControl.current?.endHold()} onVoiceCancel={() => voiceControl.current?.cancelHold()} /> : panel === "text" ?
            <Button variant="ghost" size="sm" className="rounded-full" disabled={sending || voiceBusy || textBusy} onClick={() => setPanel("service")} data-testid="service-back"><ArrowLeft className="h-4 w-4 mr-2" />Service compass</Button> : null}
          {panel === "service" && lastReceipt?.guestId === guestId && <button type="button" className="block mx-auto text-xs text-muted-foreground underline underline-offset-4" onClick={() => setSent(lastReceipt)}>View last request / feedback</button>}
          {panel === "service" && <p className="text-center text-xs text-muted-foreground">Hold to speak · release to review · up to 30 seconds. Audio is processed by OpenAI; crew playback is available for 24 hours after sending.</p>}
          {sending && <p role="status" className="text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Sending your request…</p>}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <div hidden={panel !== "text"}><GuestTextMessage key={`text-${guestId || "no-guest"}`} guestId={guestId} disabled={sending || voiceBusy || !available} onBusyChange={setTextBusy} onSent={(title, data) => setSent({ title, id: data?.id, status: data?.status, guestId })} /></div>
          <div hidden={panel !== "voice"}><GuestVoiceMessage ref={voiceControl} embedded onStateChange={setVoiceState} onBack={() => setPanel("service")} key={guestId || "no-guest"} guestId={guestId} disabled={sending || textBusy || !available} onBusyChange={setVoiceBusy} onSent={(title, data) => setSent({ title, id: data?.id, status: data?.status, guestId })} /></div>
          <p className="service-dialog__cabin"><span aria-hidden="true" />{guest.cabin || "Your cabin"}<span className="service-dialog__cabin-divider" aria-hidden="true">·</span>Onboard service</p>
        </div>}
      </DialogContent>
    </Dialog>
  );
}
